import * as React from 'react';
import { createPortal } from 'react-dom';
import { connect } from 'react-redux';
import sanitizeHtml from 'sanitize-html';
import { isFunction } from 'lodash';

import SVGDynamicIcon from 'components/svg-dynamic-icon/SVGDynamicIcon';
import ActionButton from 'components/buttons/action-button/ActionButton';
import Spinner from 'components/spinner/Spinner';

import ContentApprovalUnsortedItems from './ContentApprovalUnsortedItems';
import ContentApprovalLook from './content-approval-look/ContentApprovalLook';
import ContentApprovalAddLook from './ContentApprovalAddLook';
import ContentApprovalSort from './ContentApprovalSort';
import ContentApprovalContext from './ContentApprovalContext';

import { success } from 'redux-store/reducers/common-reducer';
import {
  addBucket,
  assignItemsToBucket,
  confirmDeleteBucket,
  confirmUnsavedClose,
  deleteItem,
  deselectAllUnsortedItems,
  editNote,
  fetchContentApprovalData,
  markContentApprovalOpen,
  resetContentApprovalModal,
  saveContentApprovalData,
  selectAllUnsortedItems,
  toggleSelectItem,
  updateLookTitle
} from 'modules/campaigns/reducers/contentApproval';

import { contentApprovalLinkSelector } from 'selectors/campaign';

import { t } from 'utils/language';
import {
  CONTENT_APPROVAL_ADD_NEW_LOOK_ID,
  CONTENT_APPROVAL_SORT_KEYS,
  CONTENT_APPROVAL_SORT_OPTIONS,
  sortLooks
} from 'utils/contentApproval';

import type {
  ClientApprovalBucket,
  ClientApprovalBucketItem,
  State as ReduxState
} from 'types';

type StateProps = {
  buckets: Array<ClientApprovalBucket>,
  unsortedItems: Array<ClientApprovalBucketItem>,
  contentApprovalLink: string | undefined | null,
  isLoading: boolean,
  isSaving: boolean,
  hasError: boolean
};

type OwnProps = {
  deliverablesContainerRef: React.RefObject<any>,
  campaignId: number,
  deliverableId: number,
  onClose: () => void
};

type DispatchProps = {
  dispatchFetchContentApprovalData: (c: number, b: number, a: string) => void,
  dispatchReset: () => void,
  dispatchSaveContentApprovalData: (b: number, a: number) => Promise<any>,
  dispatchConfirmUnsavedClose: () => Promise<any>,
  dispatchMarkContentApprovalOpen: () => void,
  dispatchShowSuccessNotification: (a: string) => void,
  dispatchToggleSelectItem: (a: number) => void,
  dispatchSelectAllUnsorted: () => void,
  dispatchDeselectAllUnsorted: () => void,
  dispatchAssignItemsToBucket: (a: number) => Promise<any>,
  dispatchItemDelete: (b: number, a: number) => void,
  dispatchBucketDelete: (a: number) => void,
  dispatchBucketCreate: (a: string) => Promise<any>,
  dispatchEditNote: (b: number, a: string) => void,
  dispatchUpdateLookTitle: (b: number, a: string) => void
};

type Props = OwnProps & StateProps & DispatchProps;

type State = {
  isModified: boolean,
  selectedBucket: {
    id: number,
    title: string
  } | null,
  currentSort: string
};

class ContentApproval extends React.PureComponent<Props, State> {
  state: State = {
    isModified: false,
    selectedBucket: null,
    currentSort: CONTENT_APPROVAL_SORT_KEYS[0]
  };

  componentDidMount() {
    const { campaignId, deliverableId, dispatchFetchContentApprovalData, dispatchMarkContentApprovalOpen } = this.props;
    const { currentSort } = this.state;

    dispatchMarkContentApprovalOpen();
    dispatchFetchContentApprovalData(campaignId, deliverableId, currentSort);
  }

  componentWillUnmount() {
    const { dispatchReset } = this.props;

    dispatchReset();
  }

  getClientPageLink(): string {
    const { contentApprovalLink } = this.props;
    const params = {
      publishSuccessLink: `<a href="${String(contentApprovalLink)}" target="_blank" rel="noopener noreferrer">${t('content_approval.publish_link_text')}</a>`
    };

    return sanitizeHtml(t('content_approval.publish_success', params), {
      allowedAttributes: {
        a: ['href', 'target', 'rel']
      }
    });
  }

  handleSave = () => {
    const {
      campaignId,
      deliverableId,
      onClose,
      dispatchSaveContentApprovalData,
      dispatchShowSuccessNotification
    } = this.props;

    dispatchSaveContentApprovalData(campaignId, deliverableId)
      .then(() => dispatchShowSuccessNotification(this.getClientPageLink()))
      .then(onClose);
  };

  handleClose = () => {
    const { dispatchConfirmUnsavedClose, onClose } = this.props;
    const { isModified } = this.state;

    if (isModified) {
      dispatchConfirmUnsavedClose()
        .then(onClose);
    } else {
      onClose();
    }
  };

  handleDeleteItem = (bucketId: number, itemId: number): void => {
    const { dispatchItemDelete } = this.props;

    this.setState({
      isModified: true
    });

    dispatchItemDelete(bucketId, itemId);
  };

  handleDeleteBucket = (bucketId: number): void => {
    const { dispatchBucketDelete } = this.props;

    this.setState({
      isModified: true
    });

    dispatchBucketDelete(bucketId);
  };

  handleCreateBucket = (title: string, callback?: any): void => {
    const { dispatchBucketCreate } = this.props;

    dispatchBucketCreate(title)
      .then(() => {
        const { buckets } = this.props;
        const bucketId = buckets[buckets.length - 1].id;

        this.setState({
          isModified: true,
          selectedBucket: {
            id: bucketId,
            title: ''
          }
        });

        if (isFunction(callback)) {
          callback();
        }
      });
  };

  handleCreateBucketFromDropdown(): void {
    const { buckets } = this.props;
    const title = t('content_approval.buckets.looks.default_title', {
      index: buckets.length + 1
    });

    this.setState({
      isModified: true
    }, () => this.handleCreateBucket(title, this.handleAssignItemsToBucket));
  }

  handleEditNote = (bucketId: number, note: string): void => {
    const { dispatchEditNote } = this.props;

    this.setState({
      isModified: true
    });

    dispatchEditNote(bucketId, note);
  };

  handleUpdateLookTitle = (bucketId: number, title: string): void => {
    const { dispatchUpdateLookTitle } = this.props;

    dispatchUpdateLookTitle(bucketId, title);
  };

  handleUnsortedItemSelect = (selectedIndex: number): void => {
    const { dispatchToggleSelectItem } = this.props;

    dispatchToggleSelectItem(selectedIndex);
  };

  handleUnsortedBucketSelect = (
    title: string,
    value: string
  ): void => {
    if (Number(value) === CONTENT_APPROVAL_ADD_NEW_LOOK_ID) {
      this.handleCreateBucketFromDropdown();
      return;
    }

    this.setState({
      isModified: true,
      selectedBucket: {
        id: Number(value),
        title
      }
    }, () => this.handleAssignItemsToBucket());
  };

  handleAssignItemsToBucket = (): void => {
    const { dispatchAssignItemsToBucket } = this.props;
    const { selectedBucket } = this.state;

    if (selectedBucket) {
      dispatchAssignItemsToBucket(selectedBucket.id);
    }
  };

  handleSort = (currentSort: string): void => {
    this.setState({
      currentSort
    });
  };

  renderLoading(): React.ReactNode {
    return (
      <div className="content-approval__spinner">
        <Spinner
          position="inline"
          size="small"
          color="light"
        />
      </div>
    );
  }

  renderUnsorted(): React.ReactNode {
    const {
      unsortedItems,
      buckets,
      dispatchSelectAllUnsorted,
      dispatchDeselectAllUnsorted
    } = this.props;
    const bucketDetails = buckets.map(bucket => ({
      id: bucket.id,
      title: bucket.title
    }));

    // `unsortedItems` as context is necessary to dynamically update data inside gallery modal
    return (
      <div className="content-approval__unsorted">
        <ContentApprovalContext.Provider
          value={{ // eslint-disable-line react/jsx-no-constructed-context-values
            type: 'unsorted',
            items: unsortedItems
          }}
        >
          <ContentApprovalUnsortedItems
            items={unsortedItems}
            bucketDetails={bucketDetails}
            onUnsortedItemSelect={this.handleUnsortedItemSelect}
            onUnsortedBucketSelect={this.handleUnsortedBucketSelect}
            onSelectAllUnsorted={dispatchSelectAllUnsorted}
            onDeselectAllUnsorted={dispatchDeselectAllUnsorted}
          />
        </ContentApprovalContext.Provider>
      </div>
    );
  }

  renderLooks(): React.ReactNode {
    const { buckets } = this.props;
    const { currentSort } = this.state;
    const updatedBuckets = sortLooks(buckets, currentSort);

    return updatedBuckets.map(bucket => (
      // `bucket.items` as context is necessary to dynamically update data inside gallery modal
      <ContentApprovalContext.Provider
        key={`content-approval__look-${bucket.id}`}
        value={{ // eslint-disable-line react/jsx-no-constructed-context-values
          type: 'looks',
          items: bucket.items
        }}
      >
        <div className="content-approval__looks">
          <ContentApprovalLook
            bucket={bucket}
            onDeleteItem={this.handleDeleteItem}
            onDeleteBucket={this.handleDeleteBucket}
            onEditNote={this.handleEditNote}
            onUpdateTitle={this.handleUpdateLookTitle}
          />
        </div>
      </ContentApprovalContext.Provider>
    ));
  }

  renderHeader(): React.ReactNode {
    return (
      <div className="content-approval__header">
        <h3 className="content-approval__title">
          {t('content_approval.title')}
        </h3>
        <div className="content-approval__subtitle">
          {t('content_approval.subtitle')}
        </div>
        <div
          className="content-approval__cross"
          onClick={this.handleClose}
        >
          <SVGDynamicIcon
            name="cross-custom-fg"
            width={14}
            height={14}
            customColor="grey"
          />
        </div>
      </div>
    );
  }

  renderFooter(): React.ReactNode {
    const { isSaving } = this.props;

    return (
      <div className="content-approval__footer">
        <ActionButton
          color="tertiary"
          onClick={this.handleClose}
        >
          {t('content_approval.cancel')}
        </ActionButton>
        <ActionButton
          color="primary"
          onClick={this.handleSave}
          disabled={isSaving}
        >
          {t('content_approval.publish')}
        </ActionButton>
      </div>
    );
  }

  renderContent(): React.ReactNode | undefined | null {
    const { buckets, hasError, isLoading } = this.props;
    const { currentSort } = this.state;

    if (hasError) {
      return null;
    }

    if (isLoading) {
      return this.renderLoading();
    }

    return (
      <div className="content-approval__content">
        {this.renderUnsorted()}
        <div className="content-approval__utility">
          <div className="content-approval__add-new">
            <ContentApprovalAddLook
              bucketsLength={buckets.length}
              onCreateBucket={this.handleCreateBucket}
            />
          </div>
          <div className="content-approval__sort">
            <ContentApprovalSort
              sortOptions={CONTENT_APPROVAL_SORT_OPTIONS.slice(0, 2)}
              currentSort={currentSort}
              onSort={this.handleSort}
            />
          </div>
        </div>
        {this.renderLooks()}
      </div>
    );
  }

  render(): React.ReactNode {
    const { deliverablesContainerRef } = this.props;

    return createPortal(
      <div className="content-approval">
        {this.renderHeader()}
        {this.renderContent()}
        {this.renderFooter()}
      </div>,
      deliverablesContainerRef.current
    );
  }
}

export const mapStateToProps = (state: ReduxState, props: OwnProps): StateProps => {
  const { contentApproval } = state;
  const deliverableState = contentApproval.deliverables[props.deliverableId] || {};
  const {
    buckets = [],
    isLoading = false,
    hasError = false,
    isSaving = false
  } = deliverableState;

  return {
    unsortedItems: buckets.length ? buckets[0].items : [],
    buckets: buckets.slice(1),
    isSaving,
    isLoading,
    hasError,
    contentApprovalLink: contentApprovalLinkSelector(state)
  };
};

export const mapDispatchToProps = (dispatch, ownProps: OwnProps): DispatchProps => ({
  dispatchFetchContentApprovalData: (campaignId: number, deliverableId: number, sort: string) => (
    dispatch(fetchContentApprovalData(campaignId, deliverableId, sort))
  ),

  dispatchReset: () => dispatch(resetContentApprovalModal(ownProps.deliverableId)),
  dispatchSaveContentApprovalData: (campaignId: number, deliverableId: number) => dispatch(saveContentApprovalData(campaignId, deliverableId)),
  dispatchConfirmUnsavedClose: () => dispatch(confirmUnsavedClose()),
  dispatchMarkContentApprovalOpen: () => dispatch(markContentApprovalOpen(ownProps.deliverableId)),
  dispatchShowSuccessNotification: (msg: string) => dispatch(success(msg)),
  dispatchToggleSelectItem: (selectedIndex: number) => dispatch(toggleSelectItem(selectedIndex, ownProps.deliverableId)),
  dispatchSelectAllUnsorted: () => dispatch(selectAllUnsortedItems(ownProps.deliverableId)),
  dispatchDeselectAllUnsorted: () => dispatch(deselectAllUnsortedItems(ownProps.deliverableId)),
  dispatchAssignItemsToBucket: (bucketId: number) => dispatch(assignItemsToBucket(bucketId, ownProps.deliverableId)),
  dispatchItemDelete: (bucketId: number, itemId: number) => dispatch(deleteItem(bucketId, itemId, ownProps.deliverableId)),
  dispatchBucketDelete: (bucketId: number) => dispatch(confirmDeleteBucket(bucketId, ownProps.deliverableId)),
  dispatchBucketCreate: (title: string) => dispatch(addBucket(title, ownProps.deliverableId)),
  dispatchEditNote: (bucketId: number, note: string) => dispatch(editNote(bucketId, note, ownProps.deliverableId)),
  dispatchUpdateLookTitle: (bucketId: number, title: string) => dispatch(updateLookTitle(bucketId, title, ownProps.deliverableId))
});

export default connect(mapStateToProps, mapDispatchToProps)(ContentApproval);
